import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Search,
    Bell,
    BookOpen,
    Clock3,
    CheckCircle2,
    Award,
    Filter,
    Sparkles,
    ChevronDown,
    Library,
    User,
    X,
    Eye,
    Download
} from "lucide-react";
import { getAllMyCourses, getCertificateList } from "../../api/api";
import { FaGraduationCap } from "react-icons/fa";

const MyCourses = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("all");
    const [searchQuery, setSearchQuery] = useState("");
    const [sortBy, setSortBy] = useState("recent");
    const [isSortOpen, setIsSortOpen] = useState(false);
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [certificates, setCertificates] = useState([]);
    const [selectedCertificate, setSelectedCertificate] = useState(null);
    const [showCertificatePopup, setShowCertificatePopup] = useState(false);
    const [showCertificatesModal, setShowCertificatesModal] = useState(false);
    const [certificatesLoading, setCertificatesLoading] = useState(false);
    const [stats, setStats] = useState({
        enrolled: 0,
        inProgress: 0,
        completed: 0,
        certificates: 0,
    });

    useEffect(() => {
        fetchCourses();
        fetchCertificates();
    }, []);

    const fetchCourses = async () => {
        try {
            setLoading(true);
            const response = await getAllMyCourses();

            if (response && response.success) {
                const mappedCourses = response.courses.map((course) => {
                    let courseStatus = "not-started";
                    let progress = 0;

                    if (course.progress_data) {
                        progress = course.progress_data.progress_percentage || 0;

                        if (progress === 100) {
                            courseStatus = "completed";
                        } else if (progress > 0) {
                            courseStatus = "in-progress";
                        } else {
                            courseStatus = "not-started";
                        }
                    }

                    const moduleCount = course.progress_data?.total_modules || 0;
                    const lectureCount = course.progress_data?.total_lectures || 0;

                    let moduleDisplay = "";
                    if (course.duration_text) {
                        moduleDisplay = course.duration_text;
                    } else if (course.duration_weeks) {
                        moduleDisplay = `${course.duration_weeks} Weeks`;
                    } else if (moduleCount > 0) {
                        moduleDisplay = `${moduleCount} Modules`;
                    }

                    return {
                        id: course.id,
                        title: course.title,
                        instructor: course.instructor_name || "Instructor",
                        image: course.image_url || "",
                        module: moduleDisplay,
                        duration: course.duration_text || "",
                        updated: course.last_updated ? new Date(course.last_updated) : new Date(),
                        updatedFormatted: course.last_updated ? new Date(course.last_updated).toLocaleDateString() : "",
                        status: course.status || "active",
                        courseId: course.id,
                        lessonId: 1,
                        price: course.price || 0,
                        discountedPrice: course.discounted_price || null,
                        earlyBirdPrice: course.early_bird_price || null,
                        rating: course.rating || 0,
                        students: course.students_enrolled || 0,
                        slug: course.slug,
                        category: course.category || "Nutrition",
                        subcategory: course.subcategory || "",
                        level: course.level || "All Levels",
                        language: course.language || "English",
                        isBestseller: course.is_bestseller || false,
                        certificateAvailable: course.certificate_available || false,
                        videoLectures: course.video_lectures || 0,
                        shortDescription: course.short_description || "",
                        longDescription: course.long_description || "",
                        courseStatus: courseStatus,
                        progress: progress,
                        totalVideos: course.progress_data?.total_videos || 0,
                        completedVideos: course.progress_data?.completed_videos || 0,
                        totalModules: course.progress_data?.total_modules || 0,
                        totalLectures: course.progress_data?.total_lectures || 0,
                        estimatedTimeRemaining: course.progress_data?.estimated_time_remaining_minutes || null,
                        completionStatus: course.progress_data?.completion_status || "not_started",
                        purchaseInfo: course.purchase_info || null,
                        tags: course.tags || [],
                        whatYouLearn: course.what_you_learn || [],
                        includes: course.includes || [],
                        requirements: course.requirements || [],
                        certificateIssued: course.purchase_info?.certificate_issued || false,
                        certificateIssuedDate: course.purchase_info?.certificate_issued_date || null,
                    };
                });

                setCourses(mappedCourses);

                if (response.stats) {
                    setStats({
                        enrolled: response.stats.total_courses || mappedCourses.length,
                        inProgress: response.stats.in_progress_courses || 0,
                        completed: response.stats.completed_courses || 0,
                        certificates: mappedCourses.filter(c => c.certificateIssued).length || 0,
                    });
                } else {
                    const enrolled = mappedCourses.length;
                    const inProgress = mappedCourses.filter(c => c.courseStatus === "in-progress").length;
                    const completed = mappedCourses.filter(c => c.courseStatus === "completed").length;

                    setStats({
                        enrolled: enrolled,
                        inProgress: inProgress,
                        completed: completed,
                        certificates: mappedCourses.filter(c => c.certificateIssued).length || 0,
                    });
                }

                setError(null);
            } else {
                setError(response?.message || "Failed to fetch courses");
            }
        } catch (err) {
            console.error("Error fetching courses:", err);
            setError(err.message || "Failed to fetch courses");
        } finally {
            setLoading(false);
        }
    };

    const fetchCertificates = async () => {
        try {
            setCertificatesLoading(true);
            const response = await getCertificateList();

            if (response && response.success) {
                setCertificates(response.certificates || []);
                setStats(prev => ({
                    ...prev,
                    certificates: response.certificates?.length || 0
                }));
            }
        } catch (err) {
            console.error("Error fetching certificates:", err);
        } finally {
            setCertificatesLoading(false);
        }
    };

    const handleContinueCourse = (course) => {
        navigate('/dashboard', {
            state: {
                activeComponent: 'courseplayer',
                courseSlug: course.slug,
                courseId: course.id
            }
        });
    };

    const handleBrowseCourses = () => {
        navigate('/all-courses');
    };

    const handleViewCertificates = () => {
        setShowCertificatesModal(true);
    };


    const handleCloseCertificatesModal = () => {
        setShowCertificatesModal(false);
    };

    const handleViewCertificate = (certificate) => {
        setSelectedCertificate(certificate);
        setShowCertificatePopup(true);
        setShowCertificatesModal(false);
    };

    const handleCloseCertificatePopup = () => {
        setShowCertificatePopup(false);
        setSelectedCertificate(null);
    };

    const handleDownloadCertificate = (certificateLink) => {
        if (certificateLink) {
            window.open(certificateLink, '_blank');
        }
    };

    // Sorting options
    const sortOptions = [
        { value: "recent", label: "Recently Added" },
        { value: "oldest", label: "Oldest First" },
        { value: "progress-high", label: "Progress: High to Low" },
        { value: "progress-low", label: "Progress: Low to High" },
        { value: "a-z", label: "A - Z" },
        { value: "z-a", label: "Z - A" },
    ];

    const getSortLabel = () => {
        const option = sortOptions.find(opt => opt.value === sortBy);
        return option ? option.label : "Sort";
    };

    const getFilteredAndSortedCourses = () => {
        let filtered = [...courses];

        if (activeTab === "in-progress") {
            filtered = filtered.filter(course => course.courseStatus === "in-progress");
        } else if (activeTab === "completed") {
            filtered = filtered.filter(course => course.courseStatus === "completed");
        } else if (activeTab === "not-started") {
            filtered = filtered.filter(course => course.courseStatus === "not-started");
        }

        if (searchQuery) {
            filtered = filtered.filter(course =>
                course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                course.instructor.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        switch (sortBy) {
            case "recent":
                filtered.sort((a, b) => b.updated - a.updated);
                break;
            case "oldest":
                filtered.sort((a, b) => a.updated - b.updated);
                break;
            case "progress-high":
                filtered.sort((a, b) => b.progress - a.progress);
                break;
            case "progress-low":
                filtered.sort((a, b) => a.progress - b.progress);
                break;
            case "a-z":
                filtered.sort((a, b) => a.title.localeCompare(b.title));
                break;
            case "z-a":
                filtered.sort((a, b) => b.title.localeCompare(a.title));
                break;
            default:
                break;
        }

        return filtered;
    };

    const filteredCourses = getFilteredAndSortedCourses();

    const getStatusCount = (status) => {
        if (status === "all") return courses.length;
        return courses.filter(course => course.courseStatus === status).length;
    };

    const statsCards = [
        {
            count: stats.enrolled,
            label: "Enrolled Courses",
            icon: BookOpen,
            bg: "bg-green-50",
            color: "text-green-600",
        },
        {
            count: stats.inProgress,
            label: "In Progress",
            icon: Clock3,
            bg: "bg-blue-50",
            color: "text-blue-600",
        },
        {
            count: stats.completed,
            label: "Completed",
            icon: CheckCircle2,
            bg: "bg-green-50",
            color: "text-green-600",
        },
        {
            count: stats.certificates,
            label: "Certificates Earned",
            icon: Award,
            bg: "bg-orange-50",
            color: "text-orange-500",
        },
    ];

    // Certificate Popup Component
    const CertificatePopup = ({ certificate, onClose, onDownload }) => {
        if (!certificate) return null;

        return (
            <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
                <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-300">
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-100">
                        <div className="flex items-center gap-3">
                            <Award size={24} className="text-green-600" />
                            <h2 className="text-xl font-bold text-gray-900">Certificate</h2>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {/* Certificate Content */}
                    <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
                        <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl p-8 border-2 border-green-200">
                            {/* Certificate Header */}
                            <div className="text-center mb-6">
                                <div className="flex justify-center mb-4">
                                    <Award size={48} className="text-green-600" />
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900">Certificate of Completion</h3>
                                <div className="w-24 h-1 bg-green-500 mx-auto mt-2 rounded-full" />
                            </div>

                            {/* Certificate Body */}
                            <div className="space-y-4">
                                <div className="text-center">
                                    <p className="text-sm text-gray-500">This certificate is awarded to</p>
                                    <p className="text-xl font-semibold text-gray-900 mt-1">
                                        {certificate.user_name || "John Doe"}
                                    </p>
                                </div>

                                <div className="text-center">
                                    <p className="text-sm text-gray-500">for successfully completing</p>
                                    <p className="text-lg font-semibold text-gray-900 mt-1">
                                        {certificate.course_name}
                                    </p>
                                </div>

                                <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-gray-200">
                                    <div>
                                        <p className="text-xs text-gray-500">Certificate ID</p>
                                        <p className="text-sm font-mono font-semibold text-gray-800 mt-1">
                                            {certificate.certificate_id}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500">Verification No.</p>
                                        <p className="text-sm font-mono font-semibold text-gray-800 mt-1">
                                            {certificate.verification_no}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500">Grade</p>
                                        <p className="text-sm font-semibold text-gray-800 mt-1">
                                            {certificate.grade || "Participant"}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500">Issue Date</p>
                                        <p className="text-sm font-semibold text-gray-800 mt-1">
                                            {certificate.issue_date ? new Date(certificate.issue_date).toLocaleDateString() : "N/A"}
                                        </p>
                                    </div>
                                </div>

                                {/* Verification Badge */}
                                <div className="flex items-center justify-center gap-2 mt-4 p-3 bg-green-100 rounded-lg">
                                    <CheckCircle2 size={16} className="text-green-600" />
                                    <span className="text-sm text-green-700 font-medium">
                                        Verified Certificate
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Footer Actions */}
                    <div className="flex items-center justify-between p-6 border-t border-gray-100 bg-gray-50">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-200 transition"
                        >
                            Close
                        </button>
                        <button
                            onClick={() => onDownload(certificate.certificate_link)}
                            className="flex items-center gap-2 px-6 py-2.5 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700 transition"
                        >
                            <Download size={18} />
                            Download Certificate
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Certificates Modal Component - Updated
    const CertificatesModal = ({ certificates, onClose, onDownload, loading }) => {
        return (
            <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
                <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-300">
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-100">
                        <div className="flex items-center gap-3">
                            <Award size={24} className="text-green-600" />
                            <h2 className="text-xl font-bold text-gray-900">My Certificates</h2>
                            <span className="px-3 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                                {certificates.length} Certificates
                            </span>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                        {loading ? (
                            <div className="flex items-center justify-center h-64">
                                <div className="text-center">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
                                    <p className="mt-4 text-gray-500">Loading certificates...</p>
                                </div>
                            </div>
                        ) : certificates.length === 0 ? (
                            <div className="text-center py-16">
                                <Award size={64} className="text-gray-300 mx-auto mb-4" />
                                <h3 className="text-xl font-semibold text-gray-700">No Certificates Yet</h3>
                                <p className="text-gray-500 mt-2">
                                    Complete your courses to earn certificates
                                </p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-4">
                                {certificates.map((cert) => (
                                    <div
                                        key={cert.id}
                                        className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-6 border border-green-200 hover:shadow-md transition-shadow"
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Award size={20} className="text-green-600" />
                                                    <h3 className="font-semibold text-gray-900">
                                                        {cert.course_name}
                                                    </h3>
                                                </div>
                                                <div className="grid grid-cols-2 gap-4 mt-3">
                                                    <div>
                                                        <p className="text-xs text-gray-500">Certificate ID</p>
                                                        <p className="text-sm font-mono font-medium text-gray-800">
                                                            {cert.certificate_id}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs text-gray-500">Verification No.</p>
                                                        <p className="text-sm font-mono font-medium text-gray-800">
                                                            {cert.verification_no}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs text-gray-500">Grade</p>
                                                        <p className="text-sm font-medium text-gray-800">
                                                            {cert.grade || "Participant"}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs text-gray-500">Issue Date</p>
                                                        <p className="text-sm font-medium text-gray-800">
                                                            {cert.issue_date ? new Date(cert.issue_date).toLocaleDateString() : "N/A"}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="ml-4">
                                                <button
                                                    onClick={() => onDownload(cert.certificate_link)}
                                                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition"
                                                >
                                                    <Download size={16} />
                                                    Download
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-end p-6 border-t border-gray-100 bg-gray-50">
                        <button
                            onClick={onClose}
                            className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-300 transition"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    };
    if (loading) {
        return (
            <div className="min-h-screen bg-[#f7f7f7] flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
                    <p className="mt-4 text-gray-500">Loading your courses...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-[#f7f7f7] flex items-center justify-center">
                <div className="text-center bg-white p-8 rounded-2xl shadow-sm">
                    <div className="text-6xl mb-4"></div>
                    <h3 className="text-xl font-semibold text-gray-700">Failed to load courses</h3>
                    <p className="text-gray-500 mt-2">{error}</p>
                    <button
                        onClick={fetchCourses}
                        className="mt-4 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#f7f7f7]">
            {/* Certificate Popup */}
            {showCertificatePopup && selectedCertificate && (
                <CertificatePopup
                    certificate={selectedCertificate}
                    onClose={handleCloseCertificatePopup}
                    onDownload={handleDownloadCertificate}
                />
            )}
            {showCertificatesModal && (
                <CertificatesModal
                    certificates={certificates}
                    onClose={handleCloseCertificatesModal}
                    onDownload={handleDownloadCertificate}
                    loading={certificatesLoading}
                />
            )}
            {/* HEADER */}
            <header className="bg-white border-b border-gray-200 px-8 py-4">
                <div className="flex items-center justify-between">
                    <h2 className="text-3xl font-bold text-gray-900">
                        My Courses
                    </h2>

                    <div className="flex items-center gap-4">
                        {/* <Bell size={18} className="text-gray-500" /> */}

                        <button
                            onClick={handleBrowseCourses}
                            className="h-10 px-5 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition"
                        >
                            Browse Courses
                        </button>
                    </div>
                </div>
            </header>

            {/* PAGE CONTENT */}
            <div className="p-8">
                {/* STATS */}
                <div className="grid grid-cols-4 gap-5 mb-8">
                    {statsCards.map((item, index) => {
                        const Icon = item.icon;

                        return (
                            <div
                                key={index}
                                className="bg-white border border-gray-200 rounded-2xl p-5"
                            >
                                <div className="flex items-center gap-4">
                                    <div
                                        className={`w-12 h-12 rounded-xl ${item.bg} flex items-center justify-center`}
                                    >
                                        <Icon size={20} className={item.color} />
                                    </div>

                                    <div>
                                        <h3 className="text-3xl font-bold">{item.count}</h3>
                                        <p className="text-sm text-gray-500">{item.label}</p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* FILTERS */}
                <div className="flex items-center justify-between mb-8">
                    <div className="flex gap-3 flex-wrap">
                        <button
                            onClick={() => setActiveTab("all")}
                            className={`px-5 h-10 rounded-full text-sm transition ${activeTab === "all"
                                ? "bg-green-600 text-white"
                                : "bg-white border text-gray-600 hover:bg-gray-50"
                                }`}
                        >
                            All Courses ({getStatusCount("all")})
                        </button>

                        <button
                            onClick={() => setActiveTab("in-progress")}
                            className={`px-5 h-10 rounded-full text-sm transition ${activeTab === "in-progress"
                                ? "bg-green-600 text-white"
                                : "bg-white border text-gray-600 hover:bg-gray-50"
                                }`}
                        >
                            In Progress ({getStatusCount("in-progress")})
                        </button>

                        <button
                            onClick={() => setActiveTab("completed")}
                            className={`px-5 h-10 rounded-full text-sm transition ${activeTab === "completed"
                                ? "bg-green-600 text-white"
                                : "bg-white border text-gray-600 hover:bg-gray-50"
                                }`}
                        >
                            Completed ({getStatusCount("completed")})
                        </button>

                        <button
                            onClick={() => setActiveTab("not-started")}
                            className={`px-5 h-10 rounded-full text-sm transition ${activeTab === "not-started"
                                ? "bg-green-600 text-white"
                                : "bg-white border text-gray-600 hover:bg-gray-50"
                                }`}
                        >
                            Not Started ({getStatusCount("not-started")})
                        </button>
                    </div>

                    <div className="flex gap-4">
                        <div className="relative">
                            <Search
                                size={16}
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                            />
                            <input
                                placeholder="Search my courses..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-[260px] h-10 pl-10 pr-4 rounded-xl border border-gray-200 bg-white outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500"
                            />
                        </div>

                        {/* Sort Dropdown */}
                        <div className="relative">
                            <button
                                onClick={() => setIsSortOpen(!isSortOpen)}
                                className="h-10 px-4 rounded-xl bg-white border flex items-center gap-2 text-sm hover:bg-gray-50 transition"
                            >
                                <Filter size={15} />
                                Sort: {getSortLabel()}
                                <ChevronDown size={14} className={`transition-transform ${isSortOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {isSortOpen && (
                                <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 z-50 overflow-hidden">
                                    {sortOptions.map((option) => (
                                        <button
                                            key={option.value}
                                            onClick={() => {
                                                setSortBy(option.value);
                                                setIsSortOpen(false);
                                            }}
                                            className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition ${sortBy === option.value ? 'bg-green-50 text-green-600 font-medium' : 'text-gray-700'
                                                }`}
                                        >
                                            {option.label}
                                            {sortBy === option.value && (
                                                <span className="float-right">✓</span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* IN PROGRESS SECTION */}
                {(activeTab === "all" || activeTab === "in-progress") && (
                    <>
                        <div className="flex items-center gap-2 mb-5">
                            <h3 className="font-semibold text-lg">In Progress</h3>
                            <span className="w-6 h-6 rounded-full bg-green-100 text-green-700 text-xs flex items-center justify-center">
                                {getStatusCount("in-progress")}
                            </span>
                        </div>

                        {filteredCourses.filter(c => c.courseStatus === "in-progress").length === 0 ? (
                            <div className="text-center py-8 bg-white rounded-2xl border border-gray-200">
                                <p className="text-gray-500">No in-progress courses found</p>
                            </div>
                        ) : (
                            filteredCourses.filter(c => c.courseStatus === "in-progress").map((course) => (
                                <div
                                    key={course.id}
                                    className="bg-white border border-gray-200 rounded-3xl overflow-hidden mb-6"
                                >
                                    <div className="grid grid-cols-[140px_1fr_180px]">
                                        <div className="relative h-full">
                                            {course.image && (
                                                <img
                                                    src={course.image}
                                                    className="h-full w-full object-cover"
                                                    alt={course.title}
                                                />
                                            )}
                                            {course.isBestseller && (
                                                <span className="absolute top-2 right-2 bg-[#6dbe45] text-white text-[9px] px-2 py-1 rounded-full font-bold">
                                                    BESTSELLER
                                                </span>
                                            )}
                                        </div>

                                        <div className="p-6">
                                            {course.isBestseller && (
                                                <span className="text-[11px] font-bold text-green-600 uppercase bg-green-50 px-2 py-0.5 rounded">
                                                    Bestseller
                                                </span>
                                            )}
                                            {course.category && (
                                                <span className="text-[11px] font-bold text-green-600 uppercase ml-2">
                                                    {course.category}
                                                </span>
                                            )}
                                            <h2 className="text-xl font-bold mt-2">{course.title}</h2>
                                            {course.instructor && (
                                                <p className="text-gray-500 mt-2">{course.instructor}</p>
                                            )}

                                            <div className="mt-6">
                                                <div className="flex justify-between text-sm mb-2">
                                                    <span>Progress</span>
                                                    <span className="text-green-600 font-semibold">
                                                        {course.progress}%
                                                    </span>
                                                </div>
                                                <div className="h-2 bg-gray-200 rounded-full">
                                                    <div
                                                        className="h-full bg-green-500 rounded-full transition-all duration-300"
                                                        style={{ width: `${course.progress}%` }}
                                                    />
                                                </div>
                                            </div>

                                            <div className="flex gap-6 mt-5 text-sm text-gray-500">
                                                {course.module && <span>{course.module}</span>}
                                                {course.duration && <span>{course.duration}</span>}
                                                {course.updatedFormatted && <span>Updated: {course.updatedFormatted}</span>}
                                            </div>
                                        </div>

                                        <div className="border-l border-gray-100 flex flex-col justify-center items-center p-6">
                                            <p className="text-sm text-gray-400 mb-2">Continue Learning</p>
                                            <button
                                                onClick={() => handleContinueCourse(course)}
                                                className="w-full h-11 rounded-xl bg-green-600 text-white font-medium hover:bg-green-700 transition"
                                            >
                                                Continue
                                            </button>
                                            <span className="text-xs text-gray-400 mt-4">
                                                {course.totalModules} Modules • {course.totalLectures} Lectures
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </>
                )}

                {/* COMPLETED SECTION */}
                {(activeTab === "all" || activeTab === "completed") && (
                    <div className="mt-10">
                        <div className="flex items-center gap-2 mb-5">
                            <h3 className="text-lg font-semibold text-[#1f2937]">Completed</h3>
                            <span className="w-5 h-5 rounded-full bg-green-100 text-green-700 text-[11px] flex items-center justify-center">
                                {getStatusCount("completed")}
                            </span>
                        </div>

                        {filteredCourses.filter(c => c.courseStatus === "completed").length === 0 ? (
                            <div className="text-center py-8 bg-white rounded-2xl border border-gray-200">
                                <p className="text-gray-500">No completed courses found</p>
                            </div>
                        ) : (
                            filteredCourses.filter(c => c.courseStatus === "completed").map((course) => {
                                // Find matching certificate for this course
                                const certificate = certificates.find(c => c.course_id === course.id || c.course_name === course.title);

                                return (
                                    <div key={course.id} className="bg-white border border-gray-200 rounded-2xl overflow-hidden mb-6">
                                        <div className="grid grid-cols-[95px_1fr_170px]">
                                            <div className="relative">
                                                {course.image && (
                                                    <img
                                                        src={course.image}
                                                        alt={course.title}
                                                        className="w-full h-[170px] object-cover"
                                                    />
                                                )}
                                                {course.isBestseller && (
                                                    <span className="absolute top-2 right-2 bg-[#6dbe45] text-white text-[9px] px-2 py-1 rounded-full font-bold">
                                                        BESTSELLER
                                                    </span>
                                                )}
                                            </div>

                                            <div className="p-5">
                                                {course.isBestseller && (
                                                    <span className="text-[11px] font-bold text-green-600 uppercase bg-green-50 px-2 py-0.5 rounded">
                                                        Bestseller
                                                    </span>
                                                )}
                                                {course.category && (
                                                    <p className="text-[11px] font-bold uppercase tracking-wide text-green-600 ml-2">
                                                        {course.category}
                                                    </p>
                                                )}
                                                <h2 className="text-[18px] font-semibold text-[#1f2937] mt-1">
                                                    {course.title}
                                                </h2>
                                                {course.instructor && (
                                                    <div className="flex items-center gap-2 mt-2 text-[13px] text-gray-500">
                                                        <User size={14} className="text-gray-500" />
                                                        <span>{course.instructor}</span>
                                                    </div>
                                                )}

                                                <div className="mt-5">
                                                    <div className="flex justify-between text-sm mb-2">
                                                        <span className="text-gray-500">Progress</span>
                                                        <span className="font-semibold text-green-600">100%</span>
                                                    </div>
                                                    <div className="h-[5px] bg-gray-200 rounded-full overflow-hidden">
                                                        <div className="w-full h-full bg-green-500 rounded-full" />
                                                    </div>
                                                </div>

                                                <div className="flex gap-6 mt-4 text-[13px] text-gray-500">
                                                    {course.module && (
                                                        <span className="flex items-center gap-1.5">
                                                            <BookOpen size={14} className="text-gray-500" />
                                                            {course.module}
                                                        </span>
                                                    )}
                                                    {course.updatedFormatted && (
                                                        <span className="flex items-center gap-1.5">
                                                            <CheckCircle2 size={14} className="text-green-500" />
                                                            Completed: {course.updatedFormatted}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="border-l border-gray-100 flex flex-col items-center justify-center p-5">
                                                <div className="flex items-center gap-2 mb-4">
                                                    <Award size={16} className="text-green-600" />
                                                    <p className="text-[13px] text-green-600 font-medium">
                                                        {certificate ? "Certificate Earned" : "Certificate Available"}
                                                    </p>
                                                </div>
                                                <button
                                                    onClick={() => {
                                                        if (certificate) {
                                                            handleViewCertificate(certificate);
                                                        } else {
                                                            // If no certificate found, fetch again and check
                                                            fetchCertificates().then(() => {
                                                                const updatedCert = certificates.find(c => c.course_id === course.id || c.course_name === course.title);
                                                                if (updatedCert) {
                                                                    handleViewCertificate(updatedCert);
                                                                }
                                                            });
                                                        }
                                                    }}
                                                    className="flex items-center gap-2 h-10 px-5 rounded-lg border border-green-200 bg-green-50 text-green-700 text-sm font-medium hover:bg-green-100 transition"
                                                >
                                                    <Eye size={16} />
                                                    {certificate ? "View Certificate" : "Get Certificate"}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}

                {/* NOT STARTED SECTION */}
                {(activeTab === "all" || activeTab === "not-started") && (
                    <div className="mt-10">
                        <div className="flex items-center gap-2 mb-5">
                            <h3 className="text-lg font-semibold text-[#1f2937]">Not Started</h3>
                            <span className="w-5 h-5 rounded-full bg-gray-100 text-gray-700 text-[11px] flex items-center justify-center">
                                {getStatusCount("not-started")}
                            </span>
                        </div>

                        {filteredCourses.filter(c => c.courseStatus === "not-started").length === 0 ? (
                            <div className="text-center py-8 bg-white rounded-2xl border border-gray-200">
                                <p className="text-gray-500">No unstarted courses found</p>
                            </div>
                        ) : (
                            filteredCourses.filter(c => c.courseStatus === "not-started").map((course) => (
                                <div key={course.id} className="bg-white border border-gray-200 rounded-2xl overflow-hidden mb-6">
                                    <div className="grid grid-cols-[140px_1fr_180px]">
                                        <div className="relative h-full">
                                            {course.image && (
                                                <img
                                                    src={course.image}
                                                    alt={course.title}
                                                    className="h-full w-full object-cover"
                                                />
                                            )}
                                            {course.isBestseller && (
                                                <span className="absolute top-2 right-2 bg-[#6dbe45] text-white text-[9px] px-2 py-1 rounded-full font-bold">
                                                    BESTSELLER
                                                </span>
                                            )}
                                        </div>

                                        <div className="p-6">
                                            {course.category && (
                                                <span className="text-[11px] font-bold text-green-600 uppercase">
                                                    {course.category}
                                                </span>
                                            )}
                                            <h2 className="text-xl font-bold mt-2">{course.title}</h2>
                                            {course.instructor && (
                                                <p className="text-gray-500 mt-2">{course.instructor}</p>
                                            )}

                                            <div className="mt-6">
                                                <div className="flex justify-between text-sm mb-2">
                                                    <span>Progress</span>
                                                    <span className="text-gray-500 font-semibold">0%</span>
                                                </div>
                                                <div className="h-2 bg-gray-200 rounded-full">
                                                    <div className="h-full bg-gray-300 rounded-full" style={{ width: "0%" }} />
                                                </div>
                                            </div>

                                            <div className="flex gap-6 mt-5 text-sm text-gray-500">
                                                {course.module && <span>{course.module}</span>}
                                                {course.duration && <span>{course.duration}</span>}
                                                {course.level && <span>Level: {course.level}</span>}
                                            </div>
                                        </div>

                                        <div className="border-l border-gray-100 flex flex-col justify-center items-center p-6">
                                            <p className="text-sm text-gray-400 mb-2">Start Learning</p>
                                            <button
                                                onClick={() => handleContinueCourse(course)}
                                                className="w-full h-11 rounded-xl bg-green-600 text-white font-medium hover:bg-green-700 transition"
                                            >
                                                Start Course
                                            </button>
                                            <span className="text-xs text-gray-400 mt-4">
                                                {course.totalLectures || 0} Lectures
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                {/* No Courses Found */}
                {filteredCourses.length === 0 && courses.length > 0 && (
                    <div className="text-center py-16">
                        <Library size={64} className="text-gray-400 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-gray-700">No courses match your filters</h3>
                        <p className="text-gray-500 mt-2">Try adjusting your filters or search query</p>
                    </div>
                )}

                {courses.length === 0 && (
                    <div className="text-center py-16 flex flex-col items-center justify-center">
                        <div className="text-6xl mb-4 text-green-600">
                            <FaGraduationCap />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-700">No courses enrolled yet</h3>
                        <p className="text-gray-500 mt-2">Start your learning journey today!</p>
                        <button
                            onClick={handleBrowseCourses}
                            className="mt-4 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                            Browse Courses
                        </button>
                    </div>
                )}

                {/* EXPLORE MORE COURSES */}
                {courses.length > 0 && (
                    <div className="mt-8 bg-[#e8f7ef] border border-[#d4eee0] rounded-2xl p-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-green-600 flex items-center justify-center text-white">
                                    <Sparkles size={24} className="text-white" />
                                </div>

                                <div>
                                    <h3 className="font-semibold text-[#1f2937]">
                                        Explore More Courses
                                    </h3>
                                    <p className="text-sm text-gray-500 mt-1">
                                        Expand your expertise with our growing library of
                                        expert-led nutrition programs.
                                    </p>
                                </div>
                            </div>

                            <div className="flex gap-3">
                                <button
                                    onClick={handleBrowseCourses}
                                    className="h-10 px-5 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700"
                                >
                                    Browse Courses
                                </button>

                                <button
                                    onClick={handleViewCertificates}
                                    className="h-10 px-5 rounded-lg bg-white border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50"
                                >
                                    View Certificates
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MyCourses;